import * as React from 'react';

import { cn } from '@/lib/cn';

export interface ScrollAreaProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional class for the content wrapper inside the scroll area */
  contentClassName?: string;
  /** Optional class for the native scrolling viewport */
  viewportClassName?: string;
  /** Optional style overrides for the native scrolling viewport */
  viewportStyle?: React.CSSProperties;
  /** Which scrollbars to render (default: both) */
  orientation?: 'vertical' | 'horizontal' | 'both';
  /** Thickness of the custom scrollbar thumb (px) */
  scrollbarThickness?: number;
  /** Inset from edges for the scrollbar tracks (px) */
  scrollbarInset?: number;
  /** Minimum thumb size (px) */
  thumbMinSize?: number;
  /** Extra offset from the top edge for the vertical track (px) */
  verticalTrackOffsetTop?: number;
  /** Extra offset from the bottom edge for the vertical track (px) */
  verticalTrackOffsetBottom?: number;
}

const DEFAULT_THICKNESS = 6;
const DEFAULT_INSET = 2;
const DEFAULT_MIN_THUMB = 24;

/**
 * Always-visible custom scroll indicators with proportional thumb sizes.
 * Uses native scrolling but hides native scrollbars for consistent visibility.
 */
export const ScrollArea = React.forwardRef<HTMLDivElement, ScrollAreaProps>(
  (
    {
      className,
      contentClassName,
      viewportClassName,
      viewportStyle,
      orientation = 'both',
      scrollbarThickness = DEFAULT_THICKNESS,
      scrollbarInset = DEFAULT_INSET,
      thumbMinSize = DEFAULT_MIN_THUMB,
      verticalTrackOffsetTop = 0,
      verticalTrackOffsetBottom = 0,
      onScroll,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const viewportRef = React.useRef<HTMLDivElement | null>(null);
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const vTrackRef = React.useRef<HTMLDivElement | null>(null);
    const vThumbRef = React.useRef<HTMLDivElement | null>(null);
    const hTrackRef = React.useRef<HTMLDivElement | null>(null);
    const hThumbRef = React.useRef<HTMLDivElement | null>(null);
    const rafRef = React.useRef<number | null>(null);
    const [isBaseUiScrollLocked, setIsBaseUiScrollLocked] = React.useState(() => {
      if (typeof document === 'undefined') return false;
      return document.documentElement.hasAttribute('data-base-ui-scroll-locked');
    });
    const [isInBaseUiPortal, setIsInBaseUiPortal] = React.useState(false);

    React.useImperativeHandle(ref, () => viewportRef.current as HTMLDivElement);

    const handleViewportRef = React.useCallback((node: HTMLDivElement | null) => {
      viewportRef.current = node;
      const nextIsInBaseUiPortal = Boolean(node?.closest('[data-base-ui-portal]'));
      setIsInBaseUiPortal((prev) => (prev === nextIsInBaseUiPortal ? prev : nextIsInBaseUiPortal));
    }, []);

    React.useEffect(() => {
      if (typeof document === 'undefined') return undefined;
      const html = document.documentElement;
      if (typeof MutationObserver === 'undefined') {
        setIsBaseUiScrollLocked(html.hasAttribute('data-base-ui-scroll-locked'));
        return undefined;
      }

      const observer = new MutationObserver(() => {
        setIsBaseUiScrollLocked(html.hasAttribute('data-base-ui-scroll-locked'));
      });
      observer.observe(html, {
        attributes: true,
        attributeFilter: ['data-base-ui-scroll-locked'],
      });
      return () => observer.disconnect();
    }, []);

    const shouldFreeze = isBaseUiScrollLocked && !isInBaseUiPortal;

    const hideTracks = React.useCallback(() => {
      const vTrack = vTrackRef.current;
      const hTrack = hTrackRef.current;
      if (vTrack) {
        vTrack.style.opacity = '0';
        vTrack.style.visibility = 'hidden';
      }
      if (hTrack) {
        hTrack.style.opacity = '0';
        hTrack.style.visibility = 'hidden';
      }
    }, []);

    const updateScrollbars = React.useCallback(() => {
      if (shouldFreeze) {
        hideTracks();
        return;
      }
      const viewport = viewportRef.current;
      if (!viewport) return;

      const vTrack = vTrackRef.current;
      const vThumb = vThumbRef.current;
      const hTrack = hTrackRef.current;
      const hThumb = hThumbRef.current;

      const { clientHeight, scrollHeight, scrollTop, clientWidth, scrollWidth, scrollLeft } =
        viewport;

      const allowVertical = orientation !== 'horizontal';
      const allowHorizontal = orientation !== 'vertical';
      const canScrollVertical = allowVertical && scrollHeight > clientHeight + 1;
      const canScrollHorizontal = allowHorizontal && scrollWidth > clientWidth + 1;

      if (vTrack && vThumb) {
        vTrack.style.opacity = canScrollVertical ? '1' : '0';
        vTrack.style.visibility = canScrollVertical ? 'visible' : 'hidden';

        if (canScrollVertical) {
          const inset = scrollbarInset;
          const thickness = scrollbarThickness;
          const verticalTop = inset + verticalTrackOffsetTop;
          const verticalBottom =
            inset + verticalTrackOffsetBottom + (canScrollHorizontal ? thickness + inset : 0);

          vTrack.style.top = `${verticalTop}px`;
          vTrack.style.right = `${inset}px`;
          vTrack.style.bottom = `${verticalBottom}px`;
          vTrack.style.width = `${thickness}px`;

          const visibleVerticalViewport = Math.max(
            0,
            clientHeight - verticalTrackOffsetTop - verticalTrackOffsetBottom,
          );
          const effectiveScrollHeight = Math.max(
            visibleVerticalViewport,
            scrollHeight - verticalTrackOffsetTop - verticalTrackOffsetBottom,
          );
          const trackLength = Math.max(
            0,
            clientHeight -
              inset * 2 -
              verticalTrackOffsetTop -
              verticalTrackOffsetBottom -
              (canScrollHorizontal ? thickness + inset : 0),
          );
          const rawSize =
            trackLength > 0 && effectiveScrollHeight > 0
              ? (visibleVerticalViewport / effectiveScrollHeight) * trackLength
              : 0;
          const thumbSize = Math.min(trackLength, Math.max(thumbMinSize, rawSize));
          const scrollRange = scrollHeight - clientHeight;
          const maxOffset = Math.max(0, trackLength - thumbSize);
          const thumbOffset = scrollRange > 0 ? (scrollTop / scrollRange) * maxOffset : 0;

          vThumb.style.height = `${thumbSize}px`;
          vThumb.style.transform = `translateY(${thumbOffset}px)`;
        }
      }

      if (hTrack && hThumb) {
        hTrack.style.opacity = canScrollHorizontal ? '1' : '0';
        hTrack.style.visibility = canScrollHorizontal ? 'visible' : 'hidden';

        if (canScrollHorizontal) {
          const inset = scrollbarInset;
          const thickness = scrollbarThickness;
          hTrack.style.left = `${inset}px`;
          hTrack.style.bottom = `${inset}px`;
          hTrack.style.right = `${inset + (canScrollVertical ? thickness + inset : 0)}px`;
          hTrack.style.height = `${thickness}px`;

          const trackLength = Math.max(
            0,
            clientWidth - inset * 2 - (canScrollVertical ? thickness + inset : 0),
          );
          const rawSize = trackLength > 0 ? (clientWidth / scrollWidth) * trackLength : 0;
          const thumbSize = Math.min(trackLength, Math.max(thumbMinSize, rawSize));
          const scrollRange = scrollWidth - clientWidth;
          const maxOffset = Math.max(0, trackLength - thumbSize);
          const thumbOffset = scrollRange > 0 ? (scrollLeft / scrollRange) * maxOffset : 0;

          hThumb.style.width = `${thumbSize}px`;
          hThumb.style.transform = `translateX(${thumbOffset}px)`;
        }
      }
    }, [
      hideTracks,
      orientation,
      scrollbarInset,
      scrollbarThickness,
      shouldFreeze,
      thumbMinSize,
      verticalTrackOffsetBottom,
      verticalTrackOffsetTop,
    ]);

    const scheduleUpdate = React.useCallback(() => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        updateScrollbars();
      });
    }, [updateScrollbars]);

    const cancelScheduledUpdate = React.useCallback(() => {
      if (rafRef.current === null) return;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }, []);

    React.useLayoutEffect(() => {
      updateScrollbars();
      const viewport = viewportRef.current;
      if (!viewport) return undefined;

      const resizeObserver =
        typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate);
      if (resizeObserver) {
        resizeObserver.observe(viewport);
        if (contentRef.current) {
          resizeObserver.observe(contentRef.current);
        }
      }

      return () => {
        cancelScheduledUpdate();
        resizeObserver?.disconnect();
      };
    }, [cancelScheduledUpdate, scheduleUpdate, updateScrollbars]);

    React.useLayoutEffect(() => {
      scheduleUpdate();
    }, [isInBaseUiPortal, scheduleUpdate, shouldFreeze]);

    React.useLayoutEffect(() => {
      scheduleUpdate();
    }, [children, scheduleUpdate]);

    const handleScroll = React.useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
        updateScrollbars();
        onScroll?.(event);
      },
      [onScroll, updateScrollbars],
    );

    const viewportOverflowClass = React.useMemo(() => {
      if (shouldFreeze) return 'overflow-hidden';
      if (orientation === 'vertical') return 'overflow-y-auto overflow-x-hidden';
      if (orientation === 'horizontal') return 'overflow-x-auto overflow-y-hidden';
      return 'overflow-auto';
    }, [orientation, shouldFreeze]);

    const wrapperStyle = React.useMemo<React.CSSProperties>(() => {
      if (!style) {
        return { overflow: 'hidden' };
      }
      const { overflow: _overflow, overflowX: _overflowX, overflowY: _overflowY, ...rest } = style;
      return { ...rest, overflow: 'hidden' };
    }, [style]);

    const baseVerticalTrackStyle = React.useMemo<React.CSSProperties>(
      () => ({
        opacity: 0,
        visibility: 'hidden',
      }),
      [],
    );

    const baseHorizontalTrackStyle = React.useMemo<React.CSSProperties>(
      () => ({
        opacity: 0,
        visibility: 'hidden',
      }),
      [],
    );

    return (
      <div
        className={cn(className, 'relative isolate flex flex-col overflow-hidden')}
        style={wrapperStyle}
      >
        <div
          ref={handleViewportRef}
          className={cn(
            'scrollbar-hidden min-h-0 w-full flex-1 touch-pan-y overscroll-contain',
            viewportOverflowClass,
            viewportClassName,
          )}
          style={viewportStyle}
          onScroll={handleScroll}
          {...props}
        >
          <div ref={contentRef} className={cn('box-border min-w-full', contentClassName)}>
            {children}
          </div>
        </div>
        <div
          ref={vTrackRef}
          data-scrollbar-track="vertical"
          className="pointer-events-none absolute rounded-full bg-background/85 supports-[backdrop-filter]:bg-background/70 supports-[backdrop-filter]:backdrop-blur-[1px]"
          style={baseVerticalTrackStyle}
        >
          <div
            ref={vThumbRef}
            className="absolute left-0 top-0 w-full rounded-full bg-muted-foreground/40 dark:bg-muted-foreground/60"
          />
        </div>
        <div
          ref={hTrackRef}
          data-scrollbar-track="horizontal"
          className="pointer-events-none absolute rounded-full bg-background/85 supports-[backdrop-filter]:bg-background/70 supports-[backdrop-filter]:backdrop-blur-[1px]"
          style={baseHorizontalTrackStyle}
        >
          <div
            ref={hThumbRef}
            className="absolute left-0 top-0 h-full rounded-full bg-muted-foreground/40 dark:bg-muted-foreground/60"
          />
        </div>
      </div>
    );
  },
);

ScrollArea.displayName = 'ScrollArea';
